import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { installationAPI } from '../api/installation';
import { toast } from 'react-toastify';
import './CreateInstallation.css';

const CreateInstallation = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    installationName: '',
    description: '',
    computerName: ''
  });
  const [loading, setLoading] = useState(false);
  const [activationCode, setActivationCode] = useState('');
  const [showActivationModal, setShowActivationModal] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await installationAPI.create(formData);
      
      if (response.success) {
        setActivationCode(response.data.activationCode);
        setShowActivationModal(true);
        toast.success('Installation created successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create installation');
    } finally {
      setLoading(false);
    }
  };

  const copyActivationCode = () => {
    navigator.clipboard.writeText(activationCode);
    toast.success('Activation code copied to clipboard!');
  };

  const handleClose = () => {
    setShowActivationModal(false);
    navigate('/installations');
  };

  return (
    <div className="create-installation-page">
      <div className="page-header">
        <h1>Create New Installation</h1>
        <p>Create an installation to get an activation code for the Windows software</p>
      </div>

      <form onSubmit={handleSubmit} className="installation-form">
        <div className="form-group">
          <label htmlFor="installationName">
            Installation Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="installationName"
            name="installationName"
            value={formData.installationName}
            onChange={handleChange}
            placeholder="e.g., Main Office Backup"
            required
          />
          <small>A descriptive name for this installation</small>
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Optional description"
            rows="3"
          />
        </div>

        <div className="form-group">
          <label htmlFor="computerName">Computer Name (Optional)</label>
          <input
            type="text"
            id="computerName"
            name="computerName"
            value={formData.computerName}
            onChange={handleChange}
            placeholder="Will be auto-detected when activated"
          />
          <small>This will be updated automatically when the software is activated</small>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/installations')}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Installation'}
          </button>
        </div>
      </form>

      {/* Activation Code Modal */}
      {showActivationModal && (
        <div className="modal-overlay" onClick={handleClose}>
          <div className="modal-content activation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✅ Installation Created Successfully!</h2>
            </div>
            
            <div className="modal-body">
              <div className="activation-info">
                <p className="info-text">
                  Share this activation code with the user to activate the Windows software:
                </p>
                
                <div className="activation-code-display">
                  <code className="activation-code">{activationCode}</code>
                  <button
                    type="button"
                    onClick={copyActivationCode}
                    className="btn-copy"
                    title="Copy to clipboard"
                  >
                    📋 Copy
                  </button>
                </div>

                <div className="activation-instructions">
                  <h3>📝 Instructions:</h3>
                  <ol>
                    <li>Install the Windows backup software on the target computer</li>
                    <li>Launch the application</li>
                    <li>Enter this activation code when prompted</li>
                    <li>The software will connect and sync with the server</li>
                  </ol>
                </div>

                <div className="warning-box">
                  <strong>⚠️ Important:</strong> Keep this activation code secure. 
                  You can view it later from the installations list.
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button onClick={handleClose} className="btn btn-primary">
                Got it, Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateInstallation;
